/* eslint-disable sort-keys */
import { ChatUserRolesEnum } from 'constants/chats';
import { RequireOnlyOne } from 'shared/types';
import { ChatUser, ChatUserStatus } from './chatsType';
import { FileDescriptor } from './fileTypes';
import { FlashphonerStoreType } from './stateTypes';

export enum CallDeviceType {
  audio = 'audio',
  video = 'video',
}

export type CallUserType = {
  fullName: string;
  isAdmin: number;
  nickname: string;
  userId: string;
  image: FileDescriptor | null;
};

export const ParticipantStatusEnum = {
  invited: 1,
  participated: 2,
  rejected: 3,
  missedStart: 4,
  inCall: 5,
  inLobby: 6,
  kicked: 7,
  missedFinish: 8,
} as const;

export type ParticipantStatus = typeof ParticipantStatusEnum[keyof typeof ParticipantStatusEnum];

export type CallParticipantState = {
  micOn?: boolean;
  camOn?: boolean;
  handUp?: boolean;
  sharingScreen?: boolean;
};

export type ParticipantState = {
  id: number;
  call: number;
  user: ChatUser['userId'];
  streamName: string;
  state: CallParticipantState;
};

export const ParticipantRolesEnum = {
  organizer: '37:301',
  admin: '37:302',
  regular: '37:303',
} as const;

export type ParticipantRoles = typeof ParticipantRolesEnum[keyof typeof ParticipantRolesEnum];

export const ParticipantPermitsEnum = {
  startCall: '82:600',
  endCall: '82:601',
  useCamera: '82:602',
  useMicrophone: '82:603',
  handUp: '82:604',
  screenSharing: '82:605',
  joinToCall: '82:606',
  participating: '82:607',
  recording: '82:608',
  viewRecords: '82:609',
  grantAdmin: '82:610',
  manageParticipants: '82:611',
  organizerRules: '82:612',
} as const;

export type ParticipantPermits = typeof ParticipantPermitsEnum[keyof typeof ParticipantPermitsEnum];

export type CallParticipant = {
  call: number;
  id: number;
  user: string;
  status: ParticipantStatus;
  roles: ParticipantRoles[];
  permits: ParticipantPermits[];
  states?: ParticipantState[];
  fullName: string;
  imageUrl: string;
};

export const CallStatusEnum = {
  calling: 'calling',
  finish: 'finish',
  received: 'received',
  ring: 'ring',
} as const;

export type CallStatusType = typeof CallStatusEnum[keyof typeof CallStatusEnum];

export type CallTypeApi = {
  id: number;
  owner: string;
  startDate: string;
  endDate: string;
  duration: string;
  format: number;
  type: number;
  status: number;
  participants: CallParticipant[];
  record: unknown;
};

export type CallType = {
  id?: CallTypeApi['id'];
  caller: CallUserType | null;
  conference: boolean;
  dialogId: number | null;
  callParticipants: CallParticipant[];
  status: CallStatusType;
  minified: boolean;
  isWaitingInLobby: boolean;
  startDate?: string;
  endDate?: string;
  duration?: string;
};

export type UserItemType = {
  onListItemClick?(userId: number | string): void;
  deleteUser?(id: number | string): void;
  userId: number | string;
  isAdmin?: boolean;
  imageUrl?: string;
  initials: string;
  userName: string;
  status?: ChatUserStatus;
  role?: ChatUserRolesEnum;
};

export type CallParticipantStatusUpdate = { status: ParticipantStatus; users: ChatUser['userId'][] };

export type CallParticipantPermitsUpdate = {
  role: ParticipantRoles;
  user: ChatUser['userId'];
  addPermits?: ParticipantPermits[];
  delPermits?: ParticipantPermits[];
};

export type SetCallParticipantPermits = RequireOnlyOne<
  CallParticipantPermitsUpdate,
  'addPermits' | 'delPermits' | 'role'
> & {
  user: CallParticipantPermitsUpdate['user'];
};

export type SetCallParticipantPermitsPayload = { permitsData: SetCallParticipantPermits } & { dialog: number };

export type CallParticipantStatesUpdate = {
  user: ChatUser['userId'];
  streamName: string;
  state: CallParticipantState;
};

export type CallParticipantItem = {
  userStatus: UserItemType['status'];
  participantStatus: CallParticipant['status'];
} & Pick<UserItemType, 'imageUrl' | 'initials' | 'userId' | 'userName'> &
  Pick<CallParticipant, 'roles'>;

export type UpdateParticipantStatusProps = {
  participant: CallParticipant;
  updates: CallParticipantStatusUpdate;
};

export type SetDevicesInitialValuesProps = {
  callParticipants: CallParticipant[];
  roomParticipants: FlashphonerStoreType['roomParticipants'];
};

export type UpdateParticipantStateProps = {
  participantStates: CallParticipant['states'];
  roomParticipants: FlashphonerStoreType['roomParticipants'];
};

export type SetInitDevicesValueProps = {
  participantState: ParticipantState;
  roomParticipants: FlashphonerStoreType['roomParticipants'];
};
